import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { StickyNote, Bell, BellRing, Trash2, X, Search, CheckSquare, Square, Clock, RefreshCw, Share2, Users, History, FileText, Paperclip, Loader2, Pencil, Plus } from 'lucide-react';
import PanelShell from './PanelShell';
import { ColumnHeader, KebabMenu } from './columnChrome';
import TaskListColumn from './TaskListColumn';
import NotesTasksSplit from './NotesTasksSplit';
import NotificationsColumn from './NotificationsColumn';
import NoteShareDialog from '../NoteShareDialog';
import RichTextEditor from '../RichTextEditor';
import AttachPickerModal from '../AttachPickerModal';
import DocumentPreviewModal from '../DocumentPreviewModal';
import Modal from '../ui/Modal';
import ReminderAttachmentList from '../ReminderAttachmentList';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDocumentBlobUrl } from '../../hooks/useDocumentBlobUrl';
import { noteService } from '../../services/noteService';
import { toggleTaskAtIndex } from '../../utils/markdownTasks';
import { documentService } from '../../services/documentService';
import { reminderService } from '../../services/reminderService';
import { parseServerDate } from '../../utils/datetime';
import { useLanguage } from '../../i18n';
import { logger } from '../../utils/logger';

// ─────────────────────────────────────────────────────────────────────────
// NotesPanel — Notes & Reminders (Fase 1). A two-column workspace: NOTES on the
// left (markdown "external brain", keyword-searchable) and REMINDERS on the
// right (the plain management list, GET /api/reminders?manage=true).
//
// The notes LIST is a title-only INDEX (New Notes Layout #309): a card shows the
// note's title (or a derived label — see noteTitleOf) and nothing of its body.
// Content is revealed only by opening the note. Search is unaffected — it still
// matches title AND content server-side, so a note is still findable by a word
// that no longer appears on any card.
//
// Responsive is CONTAINER-DRIVEN (a ResizeObserver on the body, not a viewport
// breakpoint — #142): side-by-side when the panel is wide, stacked vertically
// when it's a narrow split column or on mobile. Each column scrolls internally.
// In the Notes & Task List variant the two sections can be reordered and either
// one expanded to the panel's full height (#488) — see NotesTasksSplit.jsx.
//
// Both edit modals are centered and portaled to document.body. The REMINDER
// modal keeps the originally-mandated button column (Save FIRST, Delete stacked
// directly underneath, same width). The NOTE modal was rebuilt to the #281
// mockup — see NoteModalButtons for why its footer is a horizontal
// [Delete] [Save] row instead. New-item modals show Save only.
//
// The note editor reuses the WYSIWYG RichTextEditor (markdown round-trip, same
// as board descriptions) with the mockup's own toolbar set; storage stays
// markdown.
// ─────────────────────────────────────────────────────────────────────────

// The longest DERIVED label a card will show for an untitled note. An explicit
// title is never cut here (CSS `truncate` ellipsises it to the card's width);
// this only bounds the fallback so one runaway first line can't become a
// paragraph masquerading as a title.
const DERIVED_LABEL_MAX = 80;

// A note's display title: explicit title, else the first non-empty line of the
// (markdown) content, else the localized "Untitled note".
//
// ⚠️ `title` is NULLABLE, and since the list view shows the title and NOTHING
// else (New Notes Layout #309), an untitled note would otherwise be an unlabelled
// blank row. The chosen answer is a DERIVED label: its first content line, with
// markdown markers stripped and truncated, rendered with exactly the same styling
// as a real title — the card stays a readable index entry without inventing a
// title the user never wrote, and without reintroducing the body preview the card
// removed. (It is also what the note already did before #309, so nothing regressed
// for an untitled note.)
const noteTitleOf = (note, t) => {
    if (note.title && note.title.trim()) return note.title.trim();
    const firstLine = String(note.content || '')
        .split('\n')
        .map((l) => l.replace(/^[#>\-*\s]+/, '').trim())
        .find((l) => l.length > 0);
    if (!firstLine) return t('noteUntitled', 'Untitled note');
    return firstLine.length > DERIVED_LABEL_MAX
        ? `${firstLine.slice(0, DERIVED_LABEL_MAX).trimEnd()}…`
        : firstLine;
};

// "Mon D, HH:MM" in the viewer's local timezone (server timestamps are naive UTC).
const fmtLocal = (iso) => {
    const d = parseServerDate(iso);
    if (!d) return '';
    return d.toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
};

// ── Recurring reminders ──────────────────────────────────────────────────────
// A recurring reminder is ONE row whose `remind_at` is always its NEXT
// occurrence, so the card shows a single date and rolls forward when it fires.
// The strip is: [line clock] Date • Time AM/PM  <recurrence>  [circle arrows] —
// all monochrome, in the same muted colour as the rest of the meta line.
const REPEAT_OPTIONS = ['daily', 'weekly', 'every_n_days', 'monthly'];
const WEEKDAY_ORDER = [0, 1, 2, 3, 4, 5, 6]; // Mon=0 … Sun=6 (matches the API)

// "Monday, Aug 31 • 08:00 PM" — the fuller form the recurring strip specifies.
// One-off cards keep the existing compact `fmtLocal`.
const fmtStrip = (iso) => {
    const d = parseServerDate(iso);
    if (!d) return '';
    const date = d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
    const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${date} • ${time}`;
};

const enOrdinal = (n) => {
    if (n % 100 >= 10 && n % 100 <= 20) return `${n}th`;
    return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' })[n % 10] || 'th'}`;
};

// The localized recurrence string. Deliberately computed here rather than using
// the API's `recurrence_text` (which is English by contract, for PAI and API
// consumers) so Spanish renders in Spanish. Keep the two shapes in sync.
const recurrenceLabel = (t, language, r) => {
    if (!r?.recurrence) return '';
    if (r.recurrence === 'daily') return t('reminderRecurrenceDaily', 'Daily');
    if (r.recurrence === 'weekly') {
        return (r.recurrence_days || [])
            .slice()
            .sort((a, b) => a - b)
            .map((d) => t(`reminderDay${d}`, ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][d]))
            .join(', ');
    }
    if (r.recurrence === 'every_n_days') {
        return t('reminderRecurrenceEveryNDays', 'Every {n} days', { n: r.recurrence_interval_days });
    }
    // monthly — name the day so "monthly" is never ambiguous. The ordinal suffix
    // is English-only, so other languages get the bare number.
    const d = parseServerDate(r.remind_at);
    const day = d ? d.getDate() : null;
    if (!day) return t('reminderRepeatMonthly', 'Monthly');
    return t('reminderRecurrenceMonthly', 'Monthly on the {day}', {
        day: language === 'en' ? enOrdinal(day) : String(day),
    });
};

// Expiration Reminders (#463) — where a bounded series stands, rendered LOCALLY
// from the structured fields (the API's `recurrence_text` suffix is English, for
// PAI): "3 of 5 left" · "until Oct 24 · 3 left" · "until Oct 24 · 2 of 5 left".
// Returns '' for a series with no end (and for a one-off).
const fmtUntilDate = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
    if (!m) return '';
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    const opts = d.getFullYear() === new Date().getFullYear()
        ? { month: 'short', day: 'numeric' }
        : { month: 'short', day: 'numeric', year: 'numeric' };
    return d.toLocaleDateString(undefined, opts);
};

const seriesRemainingLabel = (t, r) => {
    if (!r?.recurrence || r.recurrence_remaining === null || r.recurrence_remaining === undefined) return '';
    const n = r.recurrence_remaining;
    const parts = [];
    if (r.recurrence_until) parts.push(t('reminderEndsUntil', 'until {date}', { date: fmtUntilDate(r.recurrence_until) }));
    if (r.recurrence_max_count) {
        const total = (r.recurrence_fired_count || 0) + n;
        parts.push(t('reminderRemainingCount', '{n} of {total} left', { n, total }));
    } else {
        parts.push(t('reminderRemainingLeft', '{n} left', { n }));
    }
    return parts.join(' · ');
};

// Short relative-ish date for a note card ("Mon D" / with year if not this year).
const fmtNoteDate = (iso) => {
    const d = parseServerDate(iso);
    if (!d) return '';
    const now = new Date();
    const opts = d.getFullYear() === now.getFullYear()
        ? { month: 'short', day: 'numeric' }
        : { month: 'short', day: 'numeric', year: 'numeric' };
    return d.toLocaleDateString(undefined, opts);
};

const pad = (n) => String(n).padStart(2, '0');
const toDateInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toTimeInput = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

// ── Centered modal — a thin skin over the shared <Modal> ────────────────────
// <Modal> portals to <body> (#225) and owns Escape: capture phase + stop, so
// PanelShell's bubble-phase handler never closes the panel underneath, and only
// the TOPMOST dialog reacts — a nested NoteShareDialog or attachment preview is
// dismissed on its own without closing this modal too (Share options #363).
// Modals are OPAQUE (`bg-pai-modal`) over the blurred scrim — a translucent
// dialog stacked on a translucent panel over a photograph is the one thing the
// design system forbids outright. `panelClassName` replaces the whole card skin.
const CenteredModal = ({ testId, onClose, children, panelClassName }) => (
    <Modal
        onClose={onClose}
        zIndex="z-[70]"
        size="none"
        surface=""
        cardTestId={testId}
        cardClassName={panelClassName || 'w-[min(92vw,560px)] max-h-[85dvh] overflow-y-auto bg-pai-modal rounded-modal shadow-modal border border-pai p-5'}
    >
        {children}
    </Modal>
);

// The note editor's footer, per the #281 mockup: ONE centered horizontal row of
// two equally-weighted, neutral OUTLINED controls — [🗑 Delete] [Save].
//
// ⚠️ This SUPERSEDES the Save-above-Delete stack the original Notes & Reminders
// card mandated, for the NOTE modal only: #281 states twice that the mockup is
// "the absolute source of truth" and to implement its layout "exactly as shown",
// and the mockup draws them side by side. The REMINDER modal is out of #281's
// scope and keeps the stacked column (ModalButtons) untouched.
//
// The only thing added over the static mockup is HOVER colour — a mockup cannot
// depict hover, and "neutral until you point at it, red on hover" is the
// platform's own destructive-affordance language (#116/#235/#241), so Delete
// keeps a destructive signal without a red box the PO didn't draw.
const NoteModalButtons = ({ t, saving, onSave, onDelete, showDelete }) => (
    <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
        {showDelete && (
            <button
                type="button"
                data-testid="note-modal-delete"
                onClick={onDelete}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 px-5 py-2 text-sm font-semibold rounded-ctl border border-pai bg-transparent text-pai-secondary transition-colors hover:text-pai-red hover:border-pai-red/50 disabled:opacity-50"
            >
                <Trash2 size={15} /> {t('modalDelete', 'Delete')}
            </button>
        )}
        <button
            type="button"
            data-testid="note-modal-save"
            onClick={onSave}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 px-7 py-2 text-sm font-semibold rounded-ctl border border-pai bg-transparent text-white transition-colors hover:bg-white/[0.07] hover:border-pai-strong disabled:opacity-50"
        >
            {saving ? t('commonSaving', 'Saving…') : t('save', 'Save')}
        </button>
    </div>
);

// The mandated Save-above-Delete button column (centered, same width, stacked).
const ModalButtons = ({ t, saving, onSave, onDelete, saveTestId, deleteTestId, showDelete }) => (
    <div className="mt-4 w-full max-w-[220px] mx-auto flex flex-col items-center gap-2">
        <button
            type="button"
            data-testid={saveTestId}
            onClick={onSave}
            disabled={saving}
            className="w-full inline-flex items-center justify-center gap-1 px-4 py-2 text-sm font-semibold rounded-ctl text-pai-void bg-white transition-colors hover:bg-white/[0.88] disabled:opacity-50"
        >
            {saving ? t('commonSaving', 'Saving…') : t('save', 'Save')}
        </button>
        {showDelete && (
            <button
                type="button"
                data-testid={deleteTestId}
                onClick={onDelete}
                disabled={saving}
                className="w-full inline-flex items-center justify-center gap-1 px-4 py-2 text-sm font-semibold rounded-ctl text-pai-red bg-pai-red/[0.08] border border-pai-red/40 transition-colors hover:bg-pai-red/[0.16] hover:border-pai-red/60 disabled:opacity-50"
            >
                <Trash2 size={15} /> {t('modalDelete', 'Delete')}
            </button>
        )}
    </div>
);

// ── Note attachments (#283) ─────────────────────────────────────────────────
//
// The paperclip lives in the title bar and is the ONLY trigger: it opens a
// two-way menu because the card asks for a picker that can "link or upload".
// Uploading goes through the same hash-dedup path the board attach uses (#117),
// so re-attaching a file the user already has links the existing document
// instead of growing a second copy in their File Manager.
const attachmentsMenuItems = (t) => [
    { key: 'upload', testId: 'note-attach-upload', label: t('attachUploadDevice', 'Upload from Device') },
    { key: 'fm', testId: 'note-attach-from-fm', label: t('attachFromFileManager', 'Select from File Manager') },
];

const uniquifyFilename = (name) => {
    const base = name || 'attachment';
    const dot = base.lastIndexOf('.');
    const stamp = `-${Date.now().toString(36)}`;
    return dot > 0 ? `${base.slice(0, dot)}${stamp}${base.slice(dot)}` : `${base}${stamp}`;
};

// Upload a file into the File Manager and return its document id, reusing an
// identical file if one is already there (SHA-256 of the CONTENT, #117).
const uploadOrReuse = async (file) => {
    try {
        const buf = await file.arrayBuffer();
        const digest = await crypto.subtle.digest('SHA-256', buf);
        const hash = Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
        const existing = await documentService.findDocumentByHash(hash);
        if (existing?.id) return existing.id;
    } catch { /* hashing unsupported / lookup failed → fall through to an upload */ }

    const send = async (f) => {
        try {
            const folder = await documentService.findOrCreateConversationUploadsFolder?.();
            if (folder?.id) return await documentService.uploadDocumentToFolder(f, folder.id);
        } catch { /* fall through to a root upload */ }
        return documentService.uploadDocument(f);
    };

    let uploaded;
    try {
        uploaded = await send(file);
    } catch (err) {
        // Global filename uniqueness → 409. Retry once disambiguated so
        // attaching never silently fails (the #92 "broken Attach File" bug).
        if (err?.status === 409 || err?.code === 'duplicate_filename') {
            uploaded = await send(new File([file], uniquifyFilename(file.name), { type: file.type }));
        } else {
            throw err;
        }
    }
    const id = uploaded?.document?.id || uploaded?.id || uploaded?.document_id;
    if (!id) throw new Error('Upload did not return a document id');
    return id;
};

// One attachment chip: a thumbnail for an image, an icon for anything else.
const AttachmentChip = ({ att, onOpen, onRemove, t }) => {
    const thumbUrl = useDocumentBlobUrl(att.document_id, !!att.is_image, { path: 'thumbnail' });
    return (
        <span
            data-testid="note-attachment"
            data-filename={att.filename || ''}
            className="group/att inline-flex items-center gap-1.5 max-w-[190px] pl-1 pr-1.5 py-1 rounded-ctl border border-pai bg-white/[0.04]"
        >
            <button
                type="button"
                data-testid="note-attachment-open"
                onClick={() => onOpen(att)}
                title={att.filename || ''}
                className="min-w-0 flex items-center gap-1.5 text-[11px] text-pai-secondary transition-colors hover:text-pai-blue"
            >
                {att.is_image && thumbUrl ? (
                    <img src={thumbUrl} alt="" className="w-5 h-5 rounded-chip object-cover flex-shrink-0" />
                ) : (
                    <FileText size={13} className="flex-shrink-0 text-pai-meta" />
                )}
                <span className="min-w-0 truncate">{att.filename || t('noteAttachmentUnnamed', 'file')}</span>
            </button>
            <button
                type="button"
                data-testid="note-attachment-remove"
                onClick={() => onRemove(att)}
                aria-label={t('noteAttachmentRemove', 'Remove attachment')}
                title={t('noteAttachmentRemove', 'Remove attachment')}
                className="flex-shrink-0 p-0.5 rounded-chip text-pai-meta transition-colors hover:text-pai-red"
            >
                <X size={12} />
            </button>
        </span>
    );
};

// ── Note edit / create modal ────────────────────────────────────────────────
// One interactive GFM checkbox inside the note's rendered view (#314).
//
// remark-gfm emits `<input type="checkbox" disabled>` for a task item; this
// replaces it with a live control. Its index is read from the DOM at click time
// — `querySelectorAll` returns document order and remark-gfm emits exactly one
// input per task item in that same order, so the Nth input is the Nth `- [ ]`
// marker in the source, however deeply nested. That beats a render-time counter,
// which React can re-run (StrictMode double-invokes) and quietly double-count.
//
// The click is stopped from bubbling: the surrounding read view opens the editor
// when clicked, and ticking a box must never do that.
//
// It is still a REAL `<input type="checkbox">` — keyboard operable, announced as
// a checkbox, and honestly `:checked` — but `appearance: none` hands its looks to
// `.rte-task-toggle`, the SAME class the WYSIWYG editor puts on its own circles
// (#314 re-open). So a pending item is a transparent outline that brightens on
// hover and a completed one a solid green disc with a white tick, identically on
// both surfaces, with no duplicated styling to drift apart.
const NoteTaskCheckbox = ({ checked, containerRef, onToggle }) => {
    const ref = useRef(null);
    const indexOfSelf = () => {
        const root = containerRef.current;
        if (!root || !ref.current) return -1;
        return Array.from(root.querySelectorAll('input[type="checkbox"]')).indexOf(ref.current);
    };
    return (
        <input
            ref={ref}
            type="checkbox"
            data-testid="note-task-checkbox"
            data-task-state={checked ? 'done' : 'pending'}
            checked={checked}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
                e.stopPropagation();
                const i = indexOfSelf();
                if (i >= 0) onToggle(i, e.target.checked);
            }}
            className="rte-task-toggle"
        />
    );
};

// The note's collaboration section, rendered INSIDE the detail view (#310).
//
// Sharing used to advertise itself on the LIST card (a hover Share button, a
// "Shared with N" / "Shared by X" line). It doesn't any more: the list is a
// title-only index, and access — who can see this, and the controls to change
// that — is a property of the NOTE, so it appears only once the note is open.
// Adding a collaborator still goes through NoteShareDialog, which carries the
// mandatory #259 "full editing privileges" warning; revoking is right here,
// beside the person it affects.
const NoteSharingSection = ({ t, isOwner, sharedBy, sharedWith, busy, onOpenShare, onRevoke }) => (
    <div
        data-testid="note-detail-sharing"
        className="mt-3 rounded-row border border-pai-subtle bg-white/[0.025] px-3 py-2"
    >
        <div className="flex items-center justify-between gap-2">
            <span className="pai-eyebrow flex items-center gap-1.5">
                <Users size={12} /> {t('noteShareCollaborators', 'Shared with')}
            </span>
            {isOwner && (
                <button
                    type="button"
                    data-testid="note-share"
                    onClick={onOpenShare}
                    title={t('noteShareTitle', 'Share Note')}
                    aria-label={t('noteShareTitle', 'Share Note')}
                    className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-chip border border-pai text-pai-secondary transition-colors hover:text-white hover:border-pai-strong"
                >
                    <Share2 size={12} /> {t('noteShareBtn', 'Share')}
                </button>
            )}
        </div>

        {!isOwner ? (
            <div data-testid="note-shared-by" className="mt-1.5 text-[12px] text-pai-blue">
                {t('noteSharedBy', 'Shared by {name}', {
                    name: sharedBy?.username || sharedBy?.email || '…',
                })}
            </div>
        ) : sharedWith.length === 0 ? (
            <div data-testid="note-sharing-empty" className="mt-1.5 text-[12px] text-pai-meta">
                {t('noteShareEmpty', 'Not shared yet. Only you can see this note.')}
            </div>
        ) : (
            <div data-testid="note-shared-with" className="mt-1.5 space-y-1">
                {sharedWith.map((s) => (
                    <div
                        key={s.user_id}
                        data-testid="note-collaborator"
                        className="flex items-center gap-2 text-[12px] text-pai-body"
                    >
                        <span className="min-w-0 flex-1 truncate">{s.username || s.email}</span>
                        <span className="flex-shrink-0 text-[10px] text-pai-meta">
                            {t('noteShareCanEdit', 'Can view and edit')}
                        </span>
                        <button
                            type="button"
                            data-testid="note-collaborator-remove"
                            disabled={busy}
                            onClick={() => onRevoke(s.user_id)}
                            title={t('noteShareRemove', 'Remove access')}
                            aria-label={t('noteShareRemove', 'Remove access')}
                            className="flex-shrink-0 p-0.5 rounded-chip text-white/70 transition-colors hover:text-pai-red disabled:opacity-50"
                        >
                            <X size={13} />
                        </button>
                    </div>
                ))}
            </div>
        )}
    </div>
);

const NoteModal = ({ note, onClose, onSaved, onDeleted, onChanged }) => {
    const { t } = useLanguage();
    const isNew = !note?.id;
    const [title, setTitle] = useState(note?.title || '');
    const draftRef = useRef(note?.content || '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // ── Read view vs. edit view (#309) ───────────────────────────────────────
    // The list is now a title-only index, so THIS is where a note is read: an
    // existing note opens RENDERED (react-markdown, the same `prose` stack as
    // every other markdown surface) and the WYSIWYG editor is entered explicitly
    // — by the header pencil or by clicking the text, the Ticket View precedent
    // (#105/#106). A NEW note has nothing to read, so it starts in edit mode.
    const [editing, setEditing] = useState(isNew);
    const readRef = useRef(null);   // the rendered view, for checkbox indexing (#314)
    // ── The note's markdown body ─────────────────────────────────────────────
    // State, because ticking a checkbox in the RENDERED view rewrites it in
    // place (#314). `bodyRef` mirrors it so the write pump always sends the
    // latest source rather than whatever a queued closure captured.
    const [body, setBody] = useState(note?.content || '');
    const bodyRef = useRef(note?.content || '');
    // Single-flight + coalescing writes (the #288 optimistic-labels lesson):
    // rapid ticks are applied locally at once and folded into at most one
    // in-flight PATCH, so N clicks can never race into a last-write-wins mess.
    const savingTasksRef = useRef(false);
    const pendingTasksRef = useRef(false);

    const pumpTaskSave = useCallback(async () => {
        if (savingTasksRef.current) { pendingTasksRef.current = true; return; }
        savingTasksRef.current = true;
        try {
            do {
                pendingTasksRef.current = false;
                // eslint-disable-next-line no-await-in-loop
                await noteService.update(note.id, { content: bodyRef.current });
            } while (pendingTasksRef.current);
            onChanged?.();   // the panel's list still holds the pre-tick body
        } catch (err) {
            logger.warn('NOTES', `checkbox save failed: ${err?.message || err}`);
            setError(t('noteSaveError', 'Failed to save the note.'));
        } finally {
            savingTasksRef.current = false;
        }
    }, [note?.id, onChanged, t]);

    // Adopt an externally-changed body (a channel turn, or a collaborator on a
    // shared note) while the note is OPEN — #251's promise, which #309 moved in
    // here with the content. Two guards, both load-bearing: never while the user
    // is EDITING (that would eat their keystrokes — the Rulebook dirty-state
    // rule), and never while one of our own checkbox writes is in flight (the
    // refetch that raced it carries the PRE-tick body and would visibly untick).
    useEffect(() => {
        const incoming = note?.content;
        if (incoming == null || editing) return;
        if (savingTasksRef.current || pendingTasksRef.current) return;
        if (incoming === bodyRef.current) return;
        bodyRef.current = incoming;
        draftRef.current = incoming;
        setBody(incoming);
    }, [note?.content, editing]);

    // Tick/untick the index-th checkbox. The index comes from the rendered DOM
    // (querySelectorAll is document order) and remark-gfm emits exactly one
    // input per task item in that same order, so it addresses the right marker
    // through nested lists and interleaved content alike.
    const toggleTask = useCallback((index, checked) => {
        const next = toggleTaskAtIndex(bodyRef.current, index, checked);
        if (next === bodyRef.current) return;
        bodyRef.current = next;
        setBody(next);            // immediate visual feedback, before any network
        draftRef.current = next;  // keep the editor's draft honest if it's opened
        if (!isNew) pumpTaskSave();
    }, [isNew, pumpTaskSave]);

    // ── Sharing, now internal to the detail view (#310) ──────────────────────
    const isOwner = note?.is_owner !== false;
    const [sharedWith, setSharedWith] = useState(note?.shared_with || []);
    const [shareOpen, setShareOpen] = useState(false);
    const [shareBusy, setShareBusy] = useState(false);

    const revokeCollaborator = async (userId) => {
        setShareBusy(true);
        try {
            await noteService.unshare(note.id, userId);
            setSharedWith((prev) => prev.filter((s) => s.user_id !== userId));
            onChanged?.();
        } catch (err) {
            logger.warn('NOTES', `unshare failed: ${err?.message || err}`);
            setError(t('noteUnshareError', 'Could not update sharing.'));
        } finally {
            setShareBusy(false);
        }
    };

    // ── Attachments (#283) ───────────────────────────────────────────────────
    // A NEW note has no id yet, so its picks are STAGED here and linked the
    // moment the note is created on Save. Disabling the paperclip until a note
    // exists would be a worse answer to "click it and upload a file".
    const [attachments, setAttachments] = useState(note?.attachments || []);
    const [staged, setStaged] = useState([]);           // [{document_id, filename, is_image}]
    const [attachMenu, setAttachMenu] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [attachBusy, setAttachBusy] = useState(false);
    const [preview, setPreview] = useState(null);
    const fileInputRef = useRef(null);
    const shownAttachments = isNew ? staged : attachments;

    const refreshAttachments = async () => {
        if (isNew) return;
        try {
            const r = await noteService.listAttachments(note.id);
            setAttachments(r?.attachments || []);
        } catch (err) {
            logger.warn('NOTES', `load attachments failed: ${err?.message || err}`);
        }
    };

    const linkDocument = async (documentId, doc) => {
        setAttachMenu(false);
        setPickerOpen(false);
        if (isNew) {
            // Stage it; the note doesn't exist yet.
            setStaged((prev) => (prev.some((a) => a.document_id === documentId) ? prev : [...prev, {
                document_id: documentId,
                filename: doc?.original_filename || doc?.filename || '',
                is_image: !!(doc?.mime_type || '').startsWith('image/'),
            }]));
            return;
        }
        try {
            await noteService.attachDocument(note.id, documentId);
            await refreshAttachments();
        } catch (err) {
            logger.warn('NOTES', `attach failed: ${err?.message || err}`);
            setError(t('noteAttachError', 'Failed to attach that file.'));
        }
    };

    const handleUploadFiles = async (files) => {
        const list = Array.from(files || []);
        if (!list.length) return;
        setAttachBusy(true);
        try {
            for (const file of list) {
                // eslint-disable-next-line no-await-in-loop
                const docId = await uploadOrReuse(file);
                // eslint-disable-next-line no-await-in-loop
                await linkDocument(docId, {
                    original_filename: file.name,
                    mime_type: file.type,
                });
            }
        } catch (err) {
            logger.warn('NOTES', `upload failed: ${err?.message || err}`);
            setError(t('noteAttachError', 'Failed to attach that file.'));
        } finally {
            setAttachBusy(false);
        }
    };

    const handleRemoveAttachment = async (att) => {
        if (isNew) {
            setStaged((prev) => prev.filter((a) => a.document_id !== att.document_id));
            return;
        }
        try {
            await noteService.detachDocument(note.id, att.document_id);
            await refreshAttachments();
        } catch (err) {
            logger.warn('NOTES', `detach failed: ${err?.message || err}`);
        }
    };

    const handleSave = async () => {
        // In READ mode the editor isn't mounted, so `body` — not the editor's
        // draft — is the current truth (a checkbox ticked in the rendered view
        // updates it, #314).
        const content = ((editing ? draftRef.current : body) || '').trim();
        if (!content) { setError(t('noteContentRequired', 'A note needs some content.')); return; }
        setSaving(true);
        setError(null);
        try {
            if (isNew) {
                const created = await noteService.create({ title: title.trim() || undefined, content });
                // Link whatever was staged while the note didn't exist yet. Best
                // effort per file: a failed link must not lose the note itself.
                for (const a of staged) {
                    try {
                        // eslint-disable-next-line no-await-in-loop
                        await noteService.attachDocument(created.id, a.document_id);
                    } catch (err) {
                        logger.warn('NOTES', `staged attach failed: ${err?.message || err}`);
                    }
                }
            } else {
                await noteService.update(note.id, { title: title.trim(), content });
            }
            onSaved();
        } catch (err) {
            logger.warn('NOTES', `save failed: ${err?.message || err}`);
            setError(t('noteSaveError', 'Failed to save the note.'));
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setSaving(true);
        try {
            await noteService.remove(note.id);
            onDeleted();
        } catch (err) {
            logger.warn('NOTES', `delete failed: ${err?.message || err}`);
            setError(t('noteSaveError', 'Failed to save the note.'));
            setSaving(false);
        }
    };

    return (
        <CenteredModal
            testId="note-edit-modal"
            onClose={onClose}
            // #281 mockup proportions: a slightly wider, softer-cornered panel with
            // roomier padding than the shared default.
            panelClassName="w-[min(94vw,600px)] max-h-[88dvh] overflow-y-auto bg-pai-modal rounded-modal shadow-modal border border-pai p-5"
        >
            <div data-testid="note-modal-header" className="flex items-center justify-between gap-2 mb-4">
                <h3 className="min-w-0 flex-1 text-[17px] font-bold text-white flex items-center gap-2">
                    {/* Sand is the Notes module's accent — it may tint an icon, and
                        this is the one place the note modal carries it. */}
                    <FileText size={17} className="flex-shrink-0 text-pai-sand" />
                    <span className="truncate">
                        {isNew
                            ? t('noteModalNewTitle', 'New Note')
                            : editing
                                ? t('noteModalEditTitle', 'Edit Note')
                                : t('noteModalViewTitle', 'Note')}
                    </span>
                </h3>
                {/* Explicit Edit affordance (#309) — clicking the rendered text
                    works too, but a discoverable control shouldn't be a secret. */}
                {!isNew && !editing && (
                    <button
                        type="button"
                        data-testid="note-edit-toggle"
                        onClick={() => setEditing(true)}
                        aria-label={t('noteEditLabel', 'Edit note')}
                        title={t('noteEditLabel', 'Edit note')}
                        className="flex-shrink-0 p-1 rounded-chip text-white/70 transition-colors hover:text-white"
                    >
                        <Pencil size={16} />
                    </button>
                )}
                {/* The universal attachment affordance (#283): white/light so it
                    blends into the header, turning a subtle light blue on hover.
                    ⚠️ That blue hover is ASSERTED (`note-attached-document.spec.js`
                    reads the computed colour and requires blue to dominate), so it
                    stays an accent even though the rest of the header is
                    monochrome — keep `hover:text-pai-blue`. */}
                <div className="relative flex-shrink-0">
                    <button
                        type="button"
                        data-testid="note-attach-button"
                        onClick={() => setAttachMenu((o) => !o)}
                        disabled={attachBusy}
                        aria-haspopup="menu"
                        aria-expanded={attachMenu}
                        aria-label={t('noteAttachLabel', 'Attach a file')}
                        title={t('noteAttachLabel', 'Attach a file')}
                        className="p-1 rounded-chip text-white/70 transition-colors hover:text-pai-blue disabled:opacity-50"
                    >
                        {attachBusy ? <Loader2 size={17} className="animate-spin" /> : <Paperclip size={17} />}
                    </button>
                    {attachMenu && (
                        <div
                            data-testid="note-attach-menu"
                            role="menu"
                            className="absolute right-0 top-full mt-1 z-10 w-56 py-1 bg-pai-modal border border-pai rounded-ctl shadow-modal-sm"
                        >
                            {attachmentsMenuItems(t).map((item) => (
                                <button
                                    key={item.key}
                                    type="button"
                                    role="menuitem"
                                    data-testid={item.testId}
                                    onClick={() => {
                                        setAttachMenu(false);
                                        if (item.key === 'upload') fileInputRef.current?.click();
                                        else setPickerOpen(true);
                                    }}
                                    className="w-full px-3 py-2 text-left text-[13px] text-pai-secondary transition-colors hover:bg-white/[0.07] hover:text-white"
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        data-testid="note-attach-input"
                        className="hidden"
                        onChange={(e) => { handleUploadFiles(e.target.files); e.target.value = ''; }}
                    />
                </div>
                <button type="button" onClick={onClose} aria-label={t('modalCancel', 'Cancel')} className="flex-shrink-0 p-1 rounded-chip text-white/70 transition-colors hover:text-white">
                    <X size={18} />
                </button>
            </div>

            {/* Clean, non-obtrusive chips directly under the title bar. */}
            {shownAttachments.length > 0 && (
                <div data-testid="note-attachments" className="mb-3 flex flex-wrap gap-1.5">
                    {shownAttachments.map((att) => (
                        <AttachmentChip
                            key={att.document_id}
                            att={att}
                            t={t}
                            onOpen={setPreview}
                            onRemove={handleRemoveAttachment}
                        />
                    ))}
                </div>
            )}

            {/* Title field — a plain bordered box per the mockup. Its focus state is
                NEUTRAL (no amber ring): the mockup shows an unaccented border, and
                the recently-updated components settled on the same convention (#289). */}
            <input
                type="text"
                data-testid="note-modal-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('noteTitlePlaceholder', 'Title (optional)')}
                className="pai-input w-full mb-3 px-3 py-2.5 text-sm"
            />

            {editing ? (
                /* WYSIWYG editor (markdown round-trip; storage stays markdown). The
                   toolbar is the #281 mockup's set plus the Checklist control the
                   #314 re-open adds — grouped with the other list controls, as the
                   card asks — passed per-instance so the board editors, which share
                   this component, keep their own (heading-first) set and never gain
                   it. `tasks` likewise gates the interactive circles to this
                   editor: without it a `- [ ]` stays literal text, exactly as the
                   board surfaces render it today. */
                <RichTextEditor
                    key={note?.id || 'new'}
                    initialMarkdown={body}
                    onChange={(md) => { draftRef.current = md; }}
                    editorTestId="note-modal-editor"
                    toolbarTestId="note-modal-toolbar"
                    toolbar={['bold', 'italic', 'bullet', 'numbered', 'checklist', 'link']}
                    tasks
                    minHeight={260}
                    placeholder={t('noteContentPlaceholder', 'Write your note…')}
                    ariaLabel={t('noteContentPlaceholder', 'Write your note…')}
                />
            ) : (
                /* The READ view (#309). Same bordered box and `prose` stack as the
                   editor, so switching modes doesn't move the layout. Its GFM task
                   lists are INTERACTIVE (#314) — see NoteTaskCheckbox. */
                <div
                    ref={readRef}
                    data-testid="note-detail-content"
                    role="button"
                    tabIndex={0}
                    onClick={() => setEditing(true)}
                    onKeyDown={(e) => { if (e.key === 'Enter') setEditing(true); }}
                    style={{ minHeight: 260 }}
                    /* ⚠️ `dark:prose-invert` is KEPT rather than flattened to a
                       bare `prose-invert`, even though this screen is dark-only.
                       The #287 fix relies on its specificity: `dark:prose-invert`
                       compiles to `.dark\:prose-invert:is(.dark *)` (0,2,0), which
                       is what lets tailwind.config's `typography.invert` code-pill
                       overrides beat the plugin's own (0,1,0) `pre code` rule.
                       Plain `prose-invert` would tie instead of win and quietly
                       regress inline-code contrast, which `backticks-bug.spec.js`
                       measures on this very surface. */
                    className="rounded-row border border-pai bg-white/[0.03] px-3 py-2 text-sm text-pai-body prose prose-sm max-w-none dark:prose-invert cursor-text overflow-y-auto"
                >
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                            // remark-gfm renders a task list as a DISABLED checkbox.
                            // Enable it and resolve which one was clicked from the
                            // live DOM (document order), so the mapping is right
                            // through nesting without any render-time counter.
                            input: (props) => (props.type === 'checkbox' ? (
                                <NoteTaskCheckbox
                                    checked={!!props.checked}
                                    containerRef={readRef}
                                    onToggle={toggleTask}
                                />
                            ) : <input {...props} />),
                            // Completed items must READ as completed. The state is
                            // taken from the item's own checkbox in the hast tree
                            // (remark-gfm makes it the list item's first child) —
                            // `node.checked` is an mdast field and does not survive
                            // into what react-markdown hands components here.
                            li: ({ node, children, ...props }) => {
                                const box = (node?.children || []).find((c) => c.tagName === 'input');
                                const done = !!box?.properties?.checked;
                                // A task item's TEXT gets its own span, mirroring
                                // the editor's markup exactly (#314 re-open) — one
                                // stylesheet then dresses both surfaces. It is not
                                // cosmetic tidiness: the "completed" dimming has to
                                // land on the text alone, because opacity on the li
                                // washes out the green circle too (a child cannot
                                // undo an ancestor's opacity), and the card asks for
                                // a SOLID green circle.
                                const kids = box
                                    ? React.Children.toArray(children).reduce((acc, child) => {
                                        if (React.isValidElement(child) && child.type === NoteTaskCheckbox) acc.toggle.push(child);
                                        else acc.text.push(child);
                                        return acc;
                                    }, { toggle: [], text: [] })
                                    : null;
                                return (
                                    <li
                                        {...props}
                                        // Same vocabulary the editor writes, so ONE
                                        // stylesheet dresses both (#314 re-open).
                                        data-task={box ? (done ? 'done' : 'pending') : undefined}
                                        className={[props.className, done && 'line-through']
                                            .filter(Boolean).join(' ') || undefined}
                                    >
                                        {kids ? (
                                            <>
                                                {kids.toggle}
                                                <span className="rte-task-text">{kids.text}</span>
                                            </>
                                        ) : children}
                                    </li>
                                );
                            },
                        }}
                    >
                        {body}
                    </ReactMarkdown>
                </div>
            )}

            {/* Access info + controls, only once the note is open (#310). A NEW
                note has no id yet, so there is nothing to share until it exists. */}
            {!isNew && (
                <NoteSharingSection
                    t={t}
                    isOwner={isOwner}
                    sharedBy={note?.shared_by}
                    sharedWith={sharedWith}
                    busy={shareBusy}
                    onOpenShare={() => setShareOpen(true)}
                    onRevoke={revokeCollaborator}
                />
            )}

            {error && <div className="mt-2 text-[12px] text-pai-red">{error}</div>}

            <NoteModalButtons
                t={t}
                saving={saving}
                onSave={handleSave}
                onDelete={handleDelete}
                // A shared-in note is fully EDITABLE (Save stays) but never
                // deletable by the recipient (#259) — the route 403s anyway.
                showDelete={!isNew && note?.is_owner !== false}
            />

            {pickerOpen && (
                <AttachPickerModal
                    onClose={() => setPickerOpen(false)}
                    onSelect={(docId, doc) => linkDocument(docId, doc)}
                    testIds={{
                        modal: 'note-attach-picker',
                        search: 'note-attach-picker-search',
                        item: 'note-attach-picker-item',
                    }}
                />
            )}
            {preview && (
                <DocumentPreviewModal
                    documentId={preview.document_id}
                    documentName={preview.filename}
                    onClose={() => setPreview(null)}
                />
            )}
            {/* Rendered from INSIDE the note modal (#310), like the attach picker
                and the document preview — sharing is reachable only from an open
                note, and the dialog keeps the mandatory #259 warning on the add
                path. */}
            {shareOpen && (
                <NoteShareDialog
                    note={{ ...note, shared_with: sharedWith }}
                    onClose={() => setShareOpen(false)}
                    onShared={(recipients) => setSharedWith((prev) => [...prev, ...recipients])}
                    onRevoked={(userId) => setSharedWith((prev) => prev.filter((s) => s.user_id !== userId))}
                    onChanged={onChanged}
                />
            )}
        </CenteredModal>
    );
};

// ── Reminder edit / create modal ────────────────────────────────────────────
const ReminderModal = ({ reminder, onClose, onSaved, onDeleted, onAttachmentsChanged }) => {
    const { t } = useLanguage();
    const isNew = !reminder?.id;
    // Attached documents (#386). Seeded from the list row; a remove updates it
    // from the server's answer (the reminder's full list afterwards) and tells
    // the panel so the card behind the modal is not stale.
    const [attachments, setAttachments] = useState(reminder?.attachments || []);
    const [attachmentPreview, setAttachmentPreview] = useState(null);
    const handleRemoveAttachment = async (att) => {
        try {
            const res = await reminderService.removeAttachment(reminder.id, att.document_id);
            setAttachments(res?.attachments || []);
            onAttachmentsChanged?.();
        } catch (err) {
            logger.warn('NOTES', `reminder attachment remove failed: ${err?.message || err}`);
            setError(t('reminderSaveError', 'Failed to save the reminder.'));
        }
    };
    const initial = useMemo(() => {
        const base = reminder?.remind_at ? parseServerDate(reminder.remind_at) : null;
        const d = base || new Date(Date.now() + 60 * 60 * 1000); // default: +1h
        return { date: toDateInput(d), time: toTimeInput(d) };
    }, [reminder]);
    const [title, setTitle] = useState(reminder?.title || '');
    const [date, setDate] = useState(initial.date);
    const [time, setTime] = useState(initial.time);
    // Repeat rule — DEFAULTS TO NONE for a new reminder, per the card: a one-off
    // must never require a decision about repeating.
    const [repeat, setRepeat] = useState(reminder?.recurrence || '');
    const [repeatDays, setRepeatDays] = useState(reminder?.recurrence_days || []);
    const [repeatInterval, setRepeatInterval] = useState(
        reminder?.recurrence_interval_days ? String(reminder.recurrence_interval_days) : '',
    );
    // Expiration Reminders (#463) — "Ends: never / after N times / on date".
    // A series carrying both limits (PAI can set both) opens on "after N times";
    // the end fields are only sent when the user CHANGES them, so opening and
    // saving such a reminder never silently drops the other limit.
    const initialEnd = useMemo(() => ({
        mode: reminder?.recurrence_max_count ? 'count' : (reminder?.recurrence_until ? 'until' : 'never'),
        count: reminder?.recurrence_max_count ? String(reminder.recurrence_max_count) : '',
        until: reminder?.recurrence_until || '',
    }), [reminder]);
    const [endMode, setEndMode] = useState(initialEnd.mode);
    const [endCount, setEndCount] = useState(initialEnd.count);
    const [endUntil, setEndUntil] = useState(initialEnd.until);
    const firedSoFar = reminder?.recurrence_fired_count || 0;
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const toggleRepeatDay = (d) => setRepeatDays((prev) => (
        prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort((a, b) => a - b)
    ));

    const handleSave = async () => {
        if (!title.trim()) { setError(t('reminderTitleRequired', 'A reminder needs a title.')); return; }
        if (!date || !time) { setError(t('reminderWhenRequired', 'Pick a date and time.')); return; }
        // The date/time inputs are LOCAL wall-clock → build a local Date and send
        // its UTC ISO instant (the BE stores naive UTC).
        const when = new Date(`${date}T${time}`);
        if (Number.isNaN(when.getTime())) { setError(t('reminderWhenRequired', 'Pick a date and time.')); return; }
        if (repeat === 'weekly' && repeatDays.length === 0) {
            setError(t('reminderRepeatDaysRequired', 'Pick at least one day of the week.')); return;
        }
        const interval = parseInt(repeatInterval, 10);
        if (repeat === 'every_n_days' && !(interval > 0)) {
            setError(t('reminderRepeatIntervalRequired', 'Enter how many days between reminders.')); return;
        }
        // 'none' (not '') on the wire so an EDIT can clear an existing repeat —
        // an omitted field means "leave the rule alone" server-side.
        const rule = {
            recurrence: repeat || 'none',
            recurrence_days: repeat === 'weekly' ? repeatDays : null,
            recurrence_interval_days: repeat === 'every_n_days' ? interval : null,
        };
        // The end of the series (#463). Only with a repeat (turning the repeat
        // off drops both limits server-side), and on an edit only when changed.
        if (repeat) {
            const count = parseInt(endCount, 10);
            if (endMode === 'count' && !(count >= 1)) {
                setError(t('reminderEndsCountRequired', 'Enter how many times it should go off.')); return;
            }
            if (endMode === 'until' && (!endUntil || endUntil < date)) {
                setError(t('reminderEndsUntilRequired', 'Pick an end date on or after the first reminder.')); return;
            }
            const endChanged = isNew
                || endMode !== initialEnd.mode
                || (endMode === 'count' && String(count) !== initialEnd.count)
                || (endMode === 'until' && endUntil !== initialEnd.until);
            // The count is the TOTAL for the series — a NEW count must exceed
            // what already went off (an untouched one is never re-validated, so a
            // finished series can still be renamed).
            if (endChanged && endMode === 'count' && !isNew && count <= firedSoFar) {
                setError(t('reminderEndsCountTooLow', 'It has already gone off {n} times — enter a bigger number.', { n: firedSoFar })); return;
            }
            if (endChanged) {
                rule.recurrence_count = endMode === 'count' ? count : null;
                rule.recurrence_until = endMode === 'until' ? endUntil : null;
            }
        }
        setSaving(true);
        setError(null);
        // Only send remind_at on an edit when the user actually changed the date
        // or time. The inputs are minute-precision, so re-sending them always
        // differs from the stored seconds-precision instant — and a changed
        // remind_at RE-ARMS the reminder (a fired one jumps from Past back to
        // the active list). A rename alone must never reschedule anything.
        const timeChanged = isNew || date !== initial.date || time !== initial.time;
        try {
            if (isNew) await reminderService.createReminder({ title: title.trim(), remind_at: when.toISOString(), ...rule });
            else await reminderService.updateReminder(reminder.id, {
                title: title.trim(),
                ...(timeChanged ? { remind_at: when.toISOString() } : {}),
                ...rule,
            });
            onSaved();
        } catch (err) {
            logger.warn('NOTES', `reminder save failed: ${err?.message || err}`);
            setError(t('reminderSaveError', 'Failed to save the reminder.'));
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setSaving(true);
        try {
            await reminderService.deleteReminder(reminder.id);
            onDeleted();
        } catch (err) {
            logger.warn('NOTES', `reminder delete failed: ${err?.message || err}`);
            setError(t('reminderSaveError', 'Failed to save the reminder.'));
            setSaving(false);
        }
    };

    return (
        // With an attachment preview open on top, Escape / backdrop dismiss the
        // PREVIEW first (CenteredModal's capture-phase handler would otherwise
        // close this modal and swallow the key before the preview saw it).
        <CenteredModal
            testId="reminder-edit-modal"
            onClose={attachmentPreview ? () => setAttachmentPreview(null) : onClose}
        >
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-[17px] font-bold text-white flex items-center gap-2">
                    {/* Violet is the Reminders module's accent. */}
                    <Bell size={16} className="text-pai-violet" />
                    {isNew ? t('reminderModalNewTitle', 'New Reminder') : t('reminderModalEditTitle', 'Edit Reminder')}
                </h3>
                <button type="button" onClick={onClose} aria-label={t('modalCancel', 'Cancel')} className="p-1 rounded-chip text-white/70 transition-colors hover:text-white">
                    <X size={18} />
                </button>
            </div>

            {!isNew && attachments.length > 0 && (
                <div className="mb-3">
                    <ReminderAttachmentList
                        attachments={attachments}
                        onOpen={setAttachmentPreview}
                        onRemove={handleRemoveAttachment}
                    />
                    <div className="mt-1 text-[11px] text-white/35">
                        {t('reminderAttachmentRemoveHint', 'Removing an attachment never deletes the file.')}
                    </div>
                </div>
            )}
            {attachmentPreview && (
                <DocumentPreviewModal
                    documentId={attachmentPreview.document_id}
                    documentName={attachmentPreview.filename}
                    onClose={() => setAttachmentPreview(null)}
                />
            )}

            <input
                type="text"
                data-testid="reminder-modal-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('reminderContentPlaceholder', 'What should I remind you about?')}
                className="pai-input w-full mb-3 px-3 py-2 text-sm"
            />

            <div className="grid grid-cols-2 gap-3">
                <label className="block">
                    <span className="pai-eyebrow">{t('reminderDateLabel', 'Date')}</span>
                    <input
                        type="date"
                        data-testid="reminder-modal-date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="pai-input mt-1 w-full px-3 py-2 text-sm tnum [color-scheme:dark]"
                    />
                </label>
                <label className="block">
                    <span className="pai-eyebrow">{t('reminderTimeLabel', 'Time')}</span>
                    <input
                        type="time"
                        data-testid="reminder-modal-time"
                        value={time}
                        onChange={(e) => setTime(e.target.value)}
                        className="pai-input mt-1 w-full px-3 py-2 text-sm tnum [color-scheme:dark]"
                    />
                </label>
            </div>

            {/* Repeat — defaults to None, so creating a one-off is unchanged. */}
            <label className="block mt-3">
                <span className="pai-eyebrow">{t('reminderRepeatLabel', 'Repeat')}</span>
                <select
                    data-testid="reminder-repeat"
                    value={repeat}
                    onChange={(e) => setRepeat(e.target.value)}
                    className="pai-input mt-1 w-full px-3 py-2 text-sm [color-scheme:dark]"
                >
                    <option value="">{t('reminderRepeatNone', 'None')}</option>
                    <option value="daily">{t('reminderRepeatDaily', 'Daily')}</option>
                    <option value="weekly">{t('reminderRepeatWeekly', 'Weekly on specific days')}</option>
                    <option value="every_n_days">{t('reminderRepeatEveryNDays', 'Every X days')}</option>
                    <option value="monthly">{t('reminderRepeatMonthly', 'Monthly')}</option>
                </select>
            </label>

            {repeat === 'weekly' && (
                <div data-testid="reminder-repeat-days" className="mt-2 flex flex-wrap gap-1.5">
                    {WEEKDAY_ORDER.map((d) => {
                        const on = repeatDays.includes(d);
                        return (
                            <button
                                key={d}
                                type="button"
                                data-testid={`reminder-repeat-day-${d}`}
                                data-selected={on ? 'true' : 'false'}
                                aria-pressed={on}
                                onClick={() => toggleRepeatDay(d)}
                                className={`px-2 py-1 text-[11px] font-semibold rounded-chip border transition-colors ${on
                                    ? 'border-pai-violet/50 bg-pai-violet/[0.12] text-pai-violet'
                                    : 'border-pai text-pai-secondary hover:border-pai-strong hover:text-white'}`}
                            >
                                {t(`reminderDay${d}`, ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][d])}
                            </button>
                        );
                    })}
                </div>
            )}

            {repeat === 'every_n_days' && (
                <div className="mt-2 flex items-center gap-2">
                    <input
                        type="number"
                        min="1"
                        max="365"
                        data-testid="reminder-repeat-interval"
                        value={repeatInterval}
                        onChange={(e) => setRepeatInterval(e.target.value)}
                        className="pai-input w-24 px-3 py-2 text-sm"
                    />
                    <span className="text-[12px] text-pai-meta">{t('reminderRepeatIntervalUnit', 'days')}</span>
                </div>
            )}

            {/* Ends (#463) — only for a repeat; defaults to Never, so an unbounded
                repeat is unchanged. */}
            {repeat && (
                <div className="mt-3">
                    <label className="block">
                        <span className="pai-eyebrow">{t('reminderEndsLabel', 'Ends')}</span>
                        <select
                            data-testid="reminder-recurrence-end"
                            value={endMode}
                            onChange={(e) => setEndMode(e.target.value)}
                            className="pai-input mt-1 w-full px-3 py-2 text-sm [color-scheme:dark]"
                        >
                            <option value="never">{t('reminderEndsNever', 'Never')}</option>
                            <option value="count">{t('reminderEndsAfterCount', 'After a number of times')}</option>
                            <option value="until">{t('reminderEndsOnDate', 'On a date')}</option>
                        </select>
                    </label>
                    {endMode === 'count' && (
                        <div className="mt-2 flex items-center gap-2">
                            <input
                                type="number"
                                min="1"
                                max="1000"
                                data-testid="reminder-recurrence-count"
                                value={endCount}
                                onChange={(e) => setEndCount(e.target.value)}
                                className="pai-input w-24 px-3 py-2 text-sm"
                            />
                            <span className="text-[12px] text-pai-meta">{t('reminderEndsCountUnit', 'times in total')}</span>
                            {!isNew && firedSoFar > 0 && (
                                <span className="text-[12px] text-white/35">
                                    · {t('reminderEndsFiredSoFar', '{n} done', { n: firedSoFar })}
                                </span>
                            )}
                        </div>
                    )}
                    {endMode === 'until' && (
                        <input
                            type="date"
                            data-testid="reminder-recurrence-until"
                            value={endUntil}
                            min={date}
                            onChange={(e) => setEndUntil(e.target.value)}
                            className="pai-input mt-2 w-full px-3 py-2 text-sm tnum [color-scheme:dark]"
                        />
                    )}
                </div>
            )}

            {error && <div className="mt-2 text-[12px] text-pai-red">{error}</div>}

            <ModalButtons
                t={t}
                saving={saving}
                onSave={handleSave}
                onDelete={handleDelete}
                saveTestId="reminder-modal-save"
                deleteTestId="reminder-modal-delete"
                showDelete={!isNew}
            />
        </CenteredModal>
    );
};

// ── Column chrome — moved to ./columnChrome.jsx (#437) so the Task List and
// Notifications columns share the exact header + kebab the Notes and Reminders
// columns use. Nothing in it changed.

// ── The panel ───────────────────────────────────────────────────────────────
//
// ONE component, TWO features since #437 (the sidebar split "Notes & Reminders"
// in two):
//   * variant 'notes'     → "Notes & Task List"          (/notes):     Notes | Task List
//   * variant 'reminders' → "Reminders & Notifications"  (/reminders): Reminders | Notifications
// The Notes and Reminders columns are byte-for-byte the columns this panel always
// rendered — same state, same modals, same testids — only which pair is shown
// changed ("the internal design and logic of the existing Notes and Reminders
// features must remain untouched"). Each variant loads only what it renders.
const NotesPanel = ({ mode = 'split', onClose, onToggleExpand, variant = 'notes', notificationFeed = null }) => {
    const { t, language } = useLanguage();
    const isNotes = variant !== 'reminders';
    const navigate = useNavigate();
    const location = useLocation();

    const [notes, setNotes] = useState([]);
    const [reminders, setReminders] = useState([]);
    // Past Reminders (#284) — fired reminders, kept 7 days then hard-deleted by
    // the backend. A separate list (not a filter over `reminders`) because the two
    // are separate server queries: the active list is upcoming-only by contract.
    const [pastReminders, setPastReminders] = useState([]);
    const [remindersView, setRemindersView] = useState('active'); // 'active' | 'past'
    const [search, setSearch] = useState('');

    const [noteModal, setNoteModal] = useState(null); // {} (new) | note | null
    const [reminderModal, setReminderModal] = useState(null);
    // A reminder attachment opened straight from a CARD (#386); the modal keeps
    // its own preview so the two can never fight over one slot.
    const [cardAttachmentPreview, setCardAttachmentPreview] = useState(null);
    // Escape closes the PREVIEW, not the panel behind it: PanelShell listens for
    // Escape on `document` too, so a capture-phase handler that stops the event
    // is the only way to keep the panel open (the #272 lesson).
    useEffect(() => {
        if (!cardAttachmentPreview) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') { e.stopPropagation(); setCardAttachmentPreview(null); }
        };
        document.addEventListener('keydown', onKey, true);
        return () => document.removeEventListener('keydown', onKey, true);
    }, [cardAttachmentPreview]);
    const [searchParams, setSearchParams] = useSearchParams();
    // Which lists have answered at least once — the deep link below must not
    // give up on a reminder before the list that holds it has loaded.
    const [loadedActive, setLoadedActive] = useState(false);
    const [loadedPast, setLoadedPast] = useState(false);

    const [noteSelect, setNoteSelect] = useState(false);
    const [reminderSelect, setReminderSelect] = useState(false);
    const [selectedNotes, setSelectedNotes] = useState(() => new Set());
    const [selectedReminders, setSelectedReminders] = useState(() => new Set());

    // Container-driven stacking (#142): a ResizeObserver on the body drives a
    // `stacked` flag when the panel is narrower than ~560px (a docked split
    // column can be narrow on a wide screen — a viewport breakpoint is wrong).
    const bodyRef = useRef(null);
    const [stacked, setStacked] = useState(false);
    useEffect(() => {
        const el = bodyRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const w = entry.contentRect?.width ?? el.clientWidth;
                setStacked(w < 560);
            }
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const loadNotes = useCallback(async (q) => {
        try {
            const res = await noteService.list({ search: q ?? search });
            const fresh = res?.notes || [];
            setNotes(fresh);
            // Keep an OPEN note in sync with the refreshed list. Since #309 the
            // body lives only in the detail view, so the live-update guarantee
            // (#251: a change made on Telegram — or by a collaborator —
            // shows up with no refresh) has to reach it there. The modal decides
            // what to do with it: it never clobbers an edit in progress.
            setNoteModal((prev) => (prev?.id ? (fresh.find((n) => n.id === prev.id) || prev) : prev));
        } catch (err) {
            logger.warn('NOTES', `load notes failed: ${err?.message || err}`);
        }
    }, [search]);

    const loadReminders = useCallback(async () => {
        try {
            const res = await reminderService.listManage();
            setReminders(res?.items || []);
            setLoadedActive(true);
        } catch (err) {
            logger.warn('NOTES', `load reminders failed: ${err?.message || err}`);
        }
    }, []);

    const loadPastReminders = useCallback(async () => {
        try {
            const res = await reminderService.listManage({ view: 'past' });
            setPastReminders(res?.items || []);
            setLoadedPast(true);
        } catch (err) {
            logger.warn('NOTES', `load past reminders failed: ${err?.message || err}`);
        }
    }, []);

    // Debounced search refetch.
    useEffect(() => {
        if (!isNotes) return undefined;
        const id = setTimeout(() => loadNotes(search), 200);
        return () => clearTimeout(id);
    }, [search, loadNotes, isNotes]);

    useEffect(() => { if (!isNotes) loadReminders(); }, [loadReminders, isNotes]);

    // Deep link: /notes?reminder=<id> opens that reminder with its attachments —
    // the link a Telegram push carries when a reminder has files (#386).
    // Read-act-strip, like /boards?board=. A FIRED reminder lives in the Past
    // list, which is only fetched on demand, so it is fetched here when the
    // active list doesn't have the id; the param is consumed only once the row
    // is found — or once BOTH lists have answered without it (a reminder purged
    // after 7 days, or a stale link), so a dead link can never linger.
    const deepLinkReminder = searchParams.get('reminder');
    useEffect(() => {
        if (!deepLinkReminder) return;
        // Reminders moved to their own feature (#437). A push already sitting on a
        // phone still carries the old /notes?reminder=<id> link — forward it,
        // query intact, to where the reminder now lives.
        if (isNotes) {
            navigate(`/reminders${location.search}`, { replace: true });
            return;
        }
        const strip = () => {
            const next = new URLSearchParams(searchParams);
            next.delete('reminder');
            setSearchParams(next, { replace: true });
        };
        const active = reminders.find((r) => r.id === deepLinkReminder);
        if (active) {
            setRemindersView('active');
            setReminderModal(active);
            strip();
            return;
        }
        const past = pastReminders.find((r) => r.id === deepLinkReminder);
        if (past) {
            setRemindersView('past');
            setReminderModal(past);
            strip();
            return;
        }
        if (loadedActive && !loadedPast) {
            loadPastReminders();
            return;
        }
        if (loadedActive && loadedPast) strip();
    }, [deepLinkReminder, reminders, pastReminders, loadedActive, loadedPast,
        loadPastReminders, searchParams, setSearchParams, isNotes, navigate, location.search]);
    // The past list is fetched when its view is opened, and kept fresh only while
    // it is on screen (a reminder firing moves a row between the two lists).
    useEffect(() => { if (remindersView === 'past') loadPastReminders(); }, [remindersView, loadPastReminders]);

    // Live sync: refetch on chat-driven note/reminder events + on window focus.
    //
    // `omnis:reminder-updated` is also what the JIT firing publishes (#291 →
    // liveEventMap `reminder_due`), so at T=0 this list re-renders with the fired
    // reminder gone from Active and present in Past — the module half of the
    // unified trigger.
    useEffect(() => {
        const onNotes = () => { if (isNotes) loadNotes(search); };
        const onReminders = () => {
            if (isNotes) return;
            loadReminders();
            if (remindersView === 'past') loadPastReminders();
        };
        const onFocus = () => { onNotes(); onReminders(); };
        window.addEventListener('omnis:note-created', onNotes);
        window.addEventListener('omnis:note-updated', onNotes);
        window.addEventListener('omnis:note-deleted', onNotes);
        window.addEventListener('omnis:reminder-created', onReminders);
        window.addEventListener('omnis:reminder-updated', onReminders);
        window.addEventListener('omnis:reminder-deleted', onReminders);
        window.addEventListener('focus', onFocus);
        return () => {
            window.removeEventListener('omnis:note-created', onNotes);
            window.removeEventListener('omnis:note-updated', onNotes);
            window.removeEventListener('omnis:note-deleted', onNotes);
            window.removeEventListener('omnis:reminder-created', onReminders);
            window.removeEventListener('omnis:reminder-updated', onReminders);
            window.removeEventListener('omnis:reminder-deleted', onReminders);
            window.removeEventListener('focus', onFocus);
        };
    }, [loadNotes, loadReminders, loadPastReminders, remindersView, search, isNotes]);

    // ── selection helpers ────────────────────────────────────────────────────
    const toggleNoteSel = (id) => setSelectedNotes((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const toggleReminderSel = (id) => setSelectedReminders((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });
    const exitNoteSelect = () => { setNoteSelect(false); setSelectedNotes(new Set()); };
    const exitReminderSelect = () => { setReminderSelect(false); setSelectedReminders(new Set()); };

    const bulkDeleteNotes = async () => {
        const ids = [...selectedNotes];
        if (!ids.length) return;
        try {
            await noteService.bulkDelete(ids);
        } catch (err) {
            logger.warn('NOTES', `bulk delete notes failed: ${err?.message || err}`);
        }
        exitNoteSelect();
        loadNotes(search);
    };
    const bulkDeleteReminders = async () => {
        const ids = [...selectedReminders];
        if (!ids.length) return;
        try {
            await reminderService.bulkDelete(ids);
        } catch (err) {
            logger.warn('NOTES', `bulk delete reminders failed: ${err?.message || err}`);
        }
        exitReminderSelect();
        // A manual delete is PERMANENT from either list (#284): from Active it
        // never reaches Past, and from Past it skips the remaining retention.
        loadReminders();
        if (remindersView === 'past') loadPastReminders();
    };

    const afterReminderMutation = () => {
        loadReminders();
        if (remindersView === 'past') loadPastReminders();
    };

    // ── column bodies ────────────────────────────────────────────────────────
    // `chrome` is the #488 layout chrome (badge drag + expand + the kebab's swap
    // item) NotesTasksSplit hands each section.
    const renderNotesColumn = ({ dragProps = null, layoutControls = null, menuItems = [] } = {}) => (
        <div data-testid="notes-column" className="flex-1 min-w-0 min-h-0 flex flex-col pai-card overflow-hidden">
            <ColumnHeader
                icon={<StickyNote size={18} />}
                accent="sand"
                title={t('notesColumnTitle', 'Notes')}
                count={notes.length}
                dragProps={dragProps}
                layoutControls={layoutControls}
                selectMode={noteSelect}
                onCancelSelect={exitNoteSelect}
                cancelTestId="notes-select-cancel"
                menu={(
                    <KebabMenu
                        buttonTestId="notes-menu-button"
                        menuTestId="notes-menu"
                        ariaLabel={t('notesMenuLabel', 'Notes menu')}
                        items={[...menuItems, {
                            key: 'select',
                            testId: 'notes-select-toggle',
                            label: noteSelect ? t('notesSelectDone', 'Cancel') : t('notesSelect', 'Select'),
                            icon: <CheckSquare size={14} />,
                            onClick: () => (noteSelect ? exitNoteSelect() : setNoteSelect(true)),
                        }]}
                    />
                )}
                t={t}
            />
            {/* #488 PO review: "New Note" left the header for a "+" beside the
                search bar (the Task List body has the same row). */}
            <div className="px-3 pt-3 pb-1 flex-shrink-0 flex items-center gap-2">
                <div className="relative flex-1 min-w-0">
                    <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35 pointer-events-none" />
                    <input
                        type="text"
                        data-testid="notes-search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t('notesSearchPlaceholder', 'Search notes…')}
                        className="pai-input w-full h-[42px] pl-9 pr-3 text-sm focus:outline-none"
                    />
                </div>
                <button
                    type="button"
                    data-testid="note-new"
                    onClick={() => setNoteModal({})}
                    aria-label={t('noteNew', 'New Note')}
                    title={t('noteNew', 'New Note')}
                    className="pai-icon-btn w-[42px] h-[42px] flex-shrink-0"
                >
                    <Plus size={18} />
                </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-2.5">
                {notes.length === 0 ? (
                    <div className="text-center text-[13px] text-white/40 py-8">
                        {search ? t('notesNoResults', 'No notes match your search.') : t('notesEmpty', 'No notes yet. Capture an idea to get started.')}
                    </div>
                ) : notes.map((n) => (
                    <div
                        key={n.id}
                        data-testid="note-card"
                        onClick={() => ((noteSelect && n.is_owner !== false) ? toggleNoteSel(n.id) : setNoteModal(n))}
                        /* A SELECTED note takes the module's sand treatment — the
                           accent tint the design reserves for a highlighted note
                           row (border .3 / wash .05, deepening to .1 on hover). */
                        className={`group cursor-pointer rounded-row border px-[18px] py-4 transition-colors duration-150 ${selectedNotes.has(n.id)
                            ? 'border-pai-sand/30 bg-pai-sand/[0.05] hover:bg-pai-sand/[0.1]'
                            : 'border-white/[0.09] bg-white/[0.025] hover:bg-white/[0.06] hover:border-white/[0.16]'}`}
                    >
                        <div className="flex items-start gap-2.5">
                            {/* A note shared WITH you is not yours to delete, so it gets no
                                checkbox — bulk-delete would silently skip it server-side,
                                which would read as a broken button. */}
                            {noteSelect && n.is_owner !== false && (
                                <span
                                    data-testid="note-checkbox"
                                    className={`mt-0.5 flex-shrink-0 transition-colors ${selectedNotes.has(n.id) ? 'text-pai-sand' : 'text-white/40'}`}
                                >
                                    {selectedNotes.has(n.id) ? <CheckSquare size={18} /> : <Square size={18} />}
                                </span>
                            )}
                            <div className="min-w-0 flex-1">
                                <div className="flex items-baseline justify-between gap-3">
                                    <div data-testid="note-card-title" className="truncate text-[15px] font-semibold leading-[1.35] text-white" title={noteTitleOf(n, t)}>{noteTitleOf(n, t)}</div>
                                    <span className="tnum text-[12.5px] text-white/[0.42] flex-shrink-0">{fmtNoteDate(n.updated_at || n.created_at)}</span>
                                </div>
                                {/* NO body preview (#309) and NO sharing indicators
                                    (#310): the list is a high-level INDEX — title only.
                                    Content AND collaboration (who it's shared with /
                                    who shared it, plus the share & revoke controls)
                                    live inside the note's detail view, which is the
                                    only place they can be acted on. Do not
                                    reintroduce `note-card-preview`, `note-shared-with`
                                    or a card-level `note-share`. */}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            {noteSelect && selectedNotes.size > 0 && (
                <div className="px-3 py-3 border-t border-pai-hairline flex-shrink-0">
                    <button
                        type="button"
                        data-testid="notes-bulk-delete"
                        onClick={bulkDeleteNotes}
                        className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[13px] font-semibold rounded-ctl text-pai-red border border-pai-red/40 bg-pai-red/[0.08] transition-colors hover:bg-pai-red/[0.16] hover:border-pai-red/60"
                    >
                        <Trash2 size={14} /> {t('notesBulkDelete', { n: selectedNotes.size })}
                    </button>
                </div>
            )}
        </div>
    );

    const now = Date.now();
    const isPastView = remindersView === 'past';
    const shownReminders = isPastView ? pastReminders : reminders;

    // One card renderer for both lists — they differ only in what the time line
    // says (a due date vs. "Fired"), so forking the markup would just be two
    // places to keep in sync.
    // `i` is the row's position in the list it is rendered from. The ACTIVE list
    // is ordered by `remind_at` ASC server-side, so row 0 of it is the NEXT-DUE
    // reminder — the one the design tints violet. It is a purely presentational
    // derivation: no extra state, no extra query.
    const renderReminderCard = (r, i) => {
        const when = parseServerDate(r.remind_at);
        const overdue = !isPastView && when && when.getTime() < now;
        const selected = selectedReminders.has(r.id);
        const nextDue = !isPastView && !overdue && !selected && i === 0;
        // Overdue keeps the system's RED — it is a negative state, not simply the
        // next one — and outranks the next-due tint when a row is both.
        const surface = selected
            ? 'border-pai-violet/50 bg-pai-violet/[0.1] hover:bg-pai-violet/[0.14]'
            : overdue
                ? 'border-pai-red/30 bg-pai-red/[0.05] hover:bg-pai-red/[0.1]'
                : nextDue
                    ? 'border-pai-violet/30 bg-pai-violet/[0.05] hover:bg-pai-violet/[0.1]'
                    : 'border-white/[0.09] bg-white/[0.025] hover:bg-white/[0.06] hover:border-white/[0.16]';
        return (
            <div
                key={r.id}
                data-testid={isPastView ? 'past-reminder-card' : 'reminder-card'}
                onClick={() => (reminderSelect ? toggleReminderSel(r.id) : setReminderModal(r))}
                className={`group cursor-pointer rounded-row border px-[18px] py-4 transition-colors duration-150 ${surface}`}
            >
                <div className="flex items-center gap-2.5">
                    {reminderSelect && (
                        <span
                            data-testid="reminder-checkbox"
                            className={`flex-shrink-0 transition-colors ${selected ? 'text-pai-violet' : 'text-white/40'}`}
                        >
                            {selected ? <CheckSquare size={18} /> : <Square size={18} />}
                        </span>
                    )}
                    <div className="min-w-0 flex-1">
                        <div className={`truncate text-[15px] font-semibold leading-[1.35] ${isPastView ? 'text-white/[0.42] line-through' : 'text-white'}`} title={r.title}>{r.title}</div>
                        {/* The meta line owns the colour; every icon inside it is an
                            outline glyph inheriting `currentColor`, which is what makes
                            the recurrence strip monochrome (asserted by
                            `recurrent-reminders.spec.js` — do not colour those icons
                            individually). The next-due row tints the whole strip
                            violet, so the due time reads in the module's accent. */}
                        <div className={`flex flex-wrap items-center gap-1.5 mt-1.5 min-w-0 text-[12.5px] ${nextDue ? 'text-pai-violet' : 'text-white/[0.42]'}`}>
                            {/* The strip: [line clock] Date • Time AM/PM  <recurrence>  [circle
                                arrows]. Both lucide icons are outline/`currentColor`, so they
                                are monochrome with the text — no fill, no coloured chip. A
                                one-off keeps the compact date and renders neither the
                                recurrence text nor the repeat icon (it "stays simple"). */}
                            {/* ⚠️ A `className` prop REPLACES lucide's own
                                `lucide lucide-clock` class (the #307 gotcha), so this
                                icon carries an explicit testid — it is the only stable
                                handle on it. */}
                            <Clock
                                size={12}
                                data-testid="reminder-card-clock"
                                className={overdue ? 'text-pai-red' : ''}
                            />
                            {/* A Past card shows the instant that FIRED and nothing about
                                the rule — a finished series (#463), which keeps its rule
                                as history, therefore reads exactly like any fired
                                reminder. */}
                            <span data-testid="reminder-card-when" className="tnum">
                                {r.recurrence && !isPastView ? fmtStrip(r.remind_at) : fmtLocal(r.remind_at)}
                            </span>
                            {r.recurrence && !isPastView && (
                                <>
                                    <span data-testid="reminder-recurrence-text">
                                        {recurrenceLabel(t, language, r)}
                                    </span>
                                    <RefreshCw
                                        size={12}
                                        data-testid="reminder-recurrence-icon"
                                        aria-label={t('reminderRecurrenceLabel', 'Repeats')}
                                    />
                                    {/* Where a bounded series stands (#463). */}
                                    {seriesRemainingLabel(t, r) && (
                                        <span data-testid="reminder-card-remaining">
                                            · {seriesRemainingLabel(t, r)}
                                        </span>
                                    )}
                                </>
                            )}
                            {overdue && <span className="text-pai-red font-semibold">· {t('reminderOverdue', 'Overdue')}</span>}
                            {isPastView && (
                                <span data-testid="past-reminder-fired" className="text-white/[0.38]">
                                    · {t('reminderFired', 'Notified')}
                                </span>
                            )}
                            {/* #291 removed the "Notification dismissed" marker that used to
                                sit here. Under JIT the Bell alert only exists once the task is
                                DUE, so dismissing it is visual cleanup of the "now" state — it
                                says nothing about the task and must not change how it looks in
                                the management list. `is_cleared` is still returned by the API;
                                nothing may style on it again. */}
                        </div>
                        {/* Attached documents (#386). Clicking a file opens it in the
                            preview; the list stops propagation so it never also opens
                            the edit modal (or toggles the row in select mode). */}
                        {r.attachments?.length > 0 && (
                            <ReminderAttachmentList
                                className="mt-2"
                                attachments={r.attachments}
                                onOpen={setCardAttachmentPreview}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const remindersColumn = (
        <div data-testid="reminders-column" className="flex-1 min-w-0 min-h-0 flex flex-col pai-card overflow-hidden">
            <ColumnHeader
                icon={<Bell size={18} />}
                accent="violet"
                title={isPastView ? t('remindersPastTitle', 'Past Reminders') : t('remindersColumnTitle', 'Reminders')}
                count={shownReminders.length}
                // No "New Reminder" while browsing the past: a new reminder is by
                // definition upcoming, so it would be created into a list the user
                // isn't looking at.
                onNew={isPastView ? undefined : () => setReminderModal({})}
                newTestId="reminder-new"
                newLabel={t('reminderNew', 'New Reminder')}
                selectMode={reminderSelect}
                onCancelSelect={exitReminderSelect}
                cancelTestId="reminders-select-cancel"
                onBack={isPastView ? () => { exitReminderSelect(); setRemindersView('active'); } : undefined}
                backTestId="reminders-past-back"
                menu={(
                    <KebabMenu
                        buttonTestId="reminders-menu-button"
                        menuTestId="reminders-menu"
                        ariaLabel={t('remindersMenuLabel', 'Reminders menu')}
                        items={[
                            {
                                key: 'select',
                                testId: 'reminders-select-toggle',
                                label: reminderSelect ? t('notesSelectDone', 'Cancel') : t('notesSelect', 'Select'),
                                icon: <CheckSquare size={14} />,
                                onClick: () => (reminderSelect ? exitReminderSelect() : setReminderSelect(true)),
                            },
                            {
                                key: 'past',
                                testId: 'reminders-menu-past',
                                label: isPastView
                                    ? t('remindersActiveView', 'Active Reminders')
                                    : t('remindersPastView', 'Past Reminders'),
                                icon: <History size={14} />,
                                onClick: () => {
                                    exitReminderSelect();
                                    setRemindersView(isPastView ? 'active' : 'past');
                                },
                            },
                        ]}
                    />
                )}
                t={t}
            />
            <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-2.5">
                {shownReminders.length === 0 ? (
                    <div
                        data-testid={isPastView ? 'reminders-past-empty' : undefined}
                        className="text-center text-[13px] text-white/40 py-8"
                    >
                        {isPastView
                            ? t('remindersPastEmpty', 'No past reminders.')
                            : t('remindersEmpty', 'No reminders yet.')}
                    </div>
                ) : shownReminders.map(renderReminderCard)}
            </div>
            {/* The 7-day rule, stated verbatim and PINNED below the list so it is
                visible whether or not the user scrolls to the bottom. */}
            {isPastView && (
                <div
                    data-testid="reminders-past-note"
                    className="px-3 py-2.5 border-t border-pai-hairline flex-shrink-0 text-[12px] text-white/[0.42] text-center"
                >
                    {t('remindersPastNote', 'These reminders will auto-eliminate in 7 days.')}
                </div>
            )}
            {reminderSelect && selectedReminders.size > 0 && (
                <div className="px-3 py-3 border-t border-pai-hairline flex-shrink-0">
                    <button
                        type="button"
                        data-testid="reminders-bulk-delete"
                        onClick={bulkDeleteReminders}
                        className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[13px] font-semibold rounded-ctl text-pai-red border border-pai-red/40 bg-pai-red/[0.08] transition-colors hover:bg-pai-red/[0.16] hover:border-pai-red/60"
                    >
                        <Trash2 size={14} /> {t('notesBulkDelete', { n: selectedReminders.size })}
                    </button>
                </div>
            )}
        </div>
    );

    return (
        <PanelShell
            title={isNotes ? t('notes', 'Notes & Task List') : t('reminders', 'Reminders & Notifications')}
            icon={isNotes ? <StickyNote size={18} /> : <BellRing size={18} />}
            mode={mode}
            onClose={onClose}
            onToggleExpand={onToggleExpand}
        >
            {isNotes ? (
                // #488: the Notes & Task List pair can be reordered and either
                // section expanded — NotesTasksSplit owns the body for this variant.
                <NotesTasksSplit
                    bodyRef={bodyRef}
                    stacked={stacked}
                    renderSection={(name, chrome) => (name === 'notes'
                        ? renderNotesColumn(chrome)
                        : <TaskListColumn {...chrome} />)}
                />
            ) : (
                <div
                    ref={bodyRef}
                    className={`h-full min-h-0 p-3 gap-3 flex ${stacked ? 'flex-col' : 'flex-row'}`}
                >
                    {remindersColumn}
                    <NotificationsColumn feed={notificationFeed} />
                </div>
            )}

            {noteModal && (
                <NoteModal
                    note={noteModal.id ? noteModal : null}
                    onClose={() => setNoteModal(null)}
                    onSaved={() => { setNoteModal(null); loadNotes(search); }}
                    onDeleted={() => { setNoteModal(null); loadNotes(search); }}
                    onChanged={() => loadNotes(search)}
                />
            )}
            {cardAttachmentPreview && (
                <DocumentPreviewModal
                    documentId={cardAttachmentPreview.document_id}
                    documentName={cardAttachmentPreview.filename}
                    onClose={() => setCardAttachmentPreview(null)}
                />
            )}
            {reminderModal && (
                <ReminderModal
                    reminder={reminderModal.id ? reminderModal : null}
                    onClose={() => setReminderModal(null)}
                    onAttachmentsChanged={afterReminderMutation}
                    // Rescheduling a PAST reminder re-arms it server-side, so it
                    // leaves this view and returns to Active — refresh both lists.
                    onSaved={() => { setReminderModal(null); afterReminderMutation(); }}
                    onDeleted={() => { setReminderModal(null); afterReminderMutation(); }}
                />
            )}
        </PanelShell>
    );
};

/** "Reminders & Notifications" (#437) — the same panel, its other variant. */
export const RemindersPanel = (props) => <NotesPanel {...props} variant="reminders" />;

export default NotesPanel;
